// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// ─────────────────────────────────────────────────────────────────────────
// NC COMPONENT CANDIDATE — "NCActionFooter", per layouts/floating-action-bar.md
// and nc-components/usage-notes/action-footer.md in the design-system repo.
// Built locally per this AU; do not import across AUs until it goes through
// review + playground + the normal issue → branch → build → MR promotion
// flow (governance/nc-component-promotion.md).
//
// A fixed, bottom-anchored action bar that stays reachable while the user
// works with the content behind it. Compose <NCActionFooter> around one or
// more <NCActionFooterBar> — do not hand-roll the mask or positioning per
// widget.
//
// `maxWidth` (design system issue #40): the wrapper is `fixed inset-0` —
// always the full browser viewport — so by default the divider/bar are
// measured from the viewport edges. That matches an ordinary full-bleed
// screen exactly, but not a layouts/stage-panel-layout.md screen, whose row
// is capped to a composed width (and changes width depending on whether the
// stage panel is currently rendering at all): on a wide viewport that row
// ends well short of the browser edge while the footer kept spanning the
// full viewport regardless, landing at a visibly different width than the
// header. Pass whatever width the header is CURRENTLY using — not a
// constant, since that width itself can change with state — to center the
// divider + bar within it instead of the viewport. Omit for an ordinary
// full-bleed screen.
// ─────────────────────────────────────────────────────────────────────────

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface NCActionFooterProps {
  children: ReactNode;
  className?: string;
  /** Caps the divider + bar to a specific row width — see the comment above
   *  for when this is needed (a layouts/stage-panel-layout.md screen). */
  maxWidth?: string;
}

export function NCActionFooter({ children, className, maxWidth }: NCActionFooterProps) {
  return (
    <div className="pointer-events-none fixed inset-0 z-[9] flex flex-col">
      <div className="flex-1" />
      <div className="pointer-events-auto bg-background">
        <div className="mx-auto" style={maxWidth ? { maxWidth } : undefined}>
          {/* mx-nc-5 mirrors NCPageHeader's own container padding, so both
              dividers share the same width and edges — within whichever
              container this div sits in (the viewport by default, or
              `maxWidth`'s capped row when provided). */}
          <div className="mx-nc-5 border-t border-border" />
          <div className={cn("flex flex-col gap-nc-3 pt-nc-3 pb-nc-6", className)}>
            <div className="mx-auto flex w-[90%] flex-col gap-nc-3">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface NCActionFooterBarProps {
  children: ReactNode;
  className?: string;
}

/**
 * One bar inside the footer. Stack more than one (e.g. a bulk-selection bar
 * plus a Save/Discard bar) — the footer's own gap-nc-3 spaces them, so there
 * is no per-bar height or position to maintain.
 */
export function NCActionFooterBar({ children, className }: NCActionFooterBarProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-nc-3 rounded-lg border border-border bg-card p-nc-4 shadow-nc-1",
        className,
      )}
    >
      {children}
    </div>
  );
}
